import React, {useEffect, useRef, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {PersistentStateId} from "../../../../store/common/Grid";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../components/grid/GridAction";
import {t} from "../../../../store/localization/TranslMessasge";
import ExtGridWrapper from "../../../../components/grid/ExtGridWrapper";
import {DataTypeDefinitionDto} from "../../../../common/apis/datatype/DataTypeDefinitionDto";
import {Add, Delete} from "@mui/icons-material";
import {DataTypeApiClient} from "../../../../common/apis/datatype/DataTypeApiClient";
import {CoreDataTypeFormatter} from "./CoreDataTypeFormatter";
import {DataTypeTranslationKey} from "./DataTypeTranslationKey";
import {CommonTranslation} from "../CommonTranslation";
import {useQuery} from "@tanstack/react-query";
import Snackbar from "../snackbar/Snackbar";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {DataTypeDetailDialog} from "./DataTypeDetailDialog";
import DataTypeDeleteDialog from "./DataTypeDeleteDialog";
import {DataTypeCreateDialog} from "./DataTypeCreateDialog";


export default function DataTypeGrid() {

    const dataTypeApiClient = useRef(new DataTypeApiClient());
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDataTypeId, setSelectedDataTypeId] = useState<string | undefined>();

    const {data, isLoading, isError, error, refetch} = useQuery({
        queryKey: ["dataType.dataTypes"],
        queryFn: () => dataTypeApiClient.current.findAll(),
    });

    useEffect(() => {
        if (isError && error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
        }
    }, [isError, error]);

    function openDetailDialog(selectedDataType: DataTypeDefinitionDto) {
        setSelectedDataTypeId(selectedDataType.id);
        setDetailDialogOpen(true);
    }

    function closeDetailDialog(updated: boolean) {
        setDetailDialogOpen(false);
        if (updated) {
            refresh();
        }
    }

    function openCreateDialog() {
        setCreateDialogOpen(true);
    }

    function openDeleteDialog(selectedDataType: DataTypeDefinitionDto) {
        setSelectedDataTypeId(selectedDataType.id);
        setDeleteDialogOpen(true);
    }

    function refresh() {
        refetch();
    }

    const columnDefinitions: GridColDef<DataTypeDefinitionDto>[] = [
        {field: 'name', headerName: t(DataTypeTranslationKey.NAME), flex: 3},
        {field: 'coreDataType', headerName: t(DataTypeTranslationKey.CORE_DATA_TYPE), valueFormatter: params => CoreDataTypeFormatter.formatCoreDataType(params.value, t), flex: 1, minWidth: 100},
        {field: '#constraints', headerName: t(DataTypeTranslationKey.NUMBER_OF_CONSTRAINTS), renderCell: params => params.row.constraints?.length ?? 0, flex: 1, minWidth: 80},
        {field: 'description', headerName: t(CommonTranslation.DESCRIPTION), flex: 3}
    ];

    let dataTypes = data ? [...data] : [];
    dataTypes.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <>
            <ExtGridWrapper
                    columns={columnDefinitions}
                    rows={dataTypes}
                    rowCount={dataTypes.length}
                    loading={isLoading}
                    getRowId={row => row.id}
                    actions={[
                        GridAction.detailButtonBuilder()
                            .onClick((_, selectedRows) => openDetailDialog(selectedRows[0] as DataTypeDefinitionDto))
                            .build(),
                        GridAction.buttonBuilder('CREATE_DATA_TYPE', ActionButtonType.IMMEDIATE, t(CommonTranslation.CREATE),
                                <Add/>)
                            .enabledPolicy(EnabledPolicy.ALWAYS)
                            .onClick(openCreateDialog)
                            .build(),
                        GridAction.buttonBuilder('DELETE_DATA_TYPE', ActionButtonType.IMMEDIATE, t(CommonTranslation.REMOVE),
                                <Delete/>)
                            .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                            .isEnabled((selectedRows) => selectedRows.length === 1 && selectedRows[0].acl?.canDelete)
                            .onClick((_, selectedRows) => openDeleteDialog(selectedRows[0]))
                            .build(),
                    ]}
                    onRowDoubleClick={(params) => openDetailDialog(params.row)}
                    peristentStateId={PersistentStateId.DATA_TYPES_PAGE_GRID}
                    resourceId={""} />

            <DataTypeCreateDialog
                    open={createDialogOpen}
                    onCreated={refresh}
                    onClose={() => setCreateDialogOpen(false)} />

            {selectedDataTypeId &&
                <>
                    <DataTypeDetailDialog
                            open={detailDialogOpen}
                            dataTypeDefinitionId={selectedDataTypeId}
                            onClose={(updated) => closeDetailDialog(updated)} />

                    <DataTypeDeleteDialog
                            open={deleteDialogOpen}
                            dataTypeDefinitionId={selectedDataTypeId}
                            onDeleted={refresh}
                            onClose={() => setDeleteDialogOpen(false)} />
                </>
            }
        </>
    );

}
